import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";

import { brand, fonts } from "../../themes/theme";
import { getTenantType } from "../../tenant/tenant";

// Colores de acento admitidos (borde izquierdo). Tokens centrales de marca.
const accentColors = {
    accent: brand.accent,
    primary: brand.primary,
};

/**
 * Celda reutilizable de la rejilla. Envuelve cualquier contenido (grafico, KPI,
 * texto) en una tarjeta de marca y controla cuantas columnas ocupa de forma
 * responsive mediante `span`.
 *
 * @param {object|number} span - columnas que ocupa por breakpoint. Numero (todas
 *   las pantallas) u objeto { xs, sm, md, lg } en unidades de 12 columnas.
 * @param {string}  title    - titulo opcional de la tarjeta (via i18n en la vista).
 * @param {string}  subtitle - subtitulo/aclaracion opcional.
 * @param {number}  minHeight- alto minimo util para graficos.
 * @param {string}  accent   - 'accent' | 'primary' | color: pinta un borde izquierdo.
 * @param {boolean} dense    - padding reducido (para tiras/KPIs compactos).
 */
function Panel({ span = 12, title, subtitle, action, minHeight, disablePadding, accent, dense, fill, sx, children }) {
    const gridColumn = normalizeSpan(span);
    const accentColor = accent ? accentColors[accent] || accent : null;
    const pad = dense ? 1.25 : 2.5;
    // En farma el subtitulo (hint tipo "porcentajes ilustrativos y sinteticos")
    // se pidio mas pequeño; en clinica se deja igual que siempre.
    const subtitleSize = getTenantType() === "farma" ? 10.5 : 12.5;

    return (
        <Box sx={{ gridColumn, ...(fill && { height: "100%", minHeight: 0, display: "flex" }), ...sx }}>
            <Card
                sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    ...(accentColor && { borderLeft: `3px solid ${accentColor}` }),
                }}
            >
                {(title || action) && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                            px: pad,
                            pt: pad,
                            pb: subtitle ? 0.25 : 0,
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            {title && (
                                <Typography
                                    sx={{
                                        fontFamily: fonts.display,
                                        fontWeight: 400,
                                        fontSize: 16,
                                        lineHeight: 1.3,
                                        color: "text.primary",
                                    }}
                                >
                                    {title}
                                </Typography>
                            )}
                            {subtitle && (
                                <Typography sx={{ fontSize: subtitleSize, color: "text.secondary", mt: 0.25 }}>
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                        {action}
                    </Box>
                )}
                <CardContent
                    sx={{
                        flexGrow: 1,
                        minHeight: fill ? 0 : minHeight,
                        display: fill ? "flex" : "block",
                        flexDirection: "column",
                        p: disablePadding ? 0 : pad,
                        "&:last-child": { pb: disablePadding ? 0 : pad },
                        ...(fill && { overflow: "hidden" }),
                    }}
                >
                    {fill ? <Box sx={{ flex: 1, minHeight: 0, height: "100%" }}>{children}</Box> : children}
                </CardContent>
            </Card>
        </Box>
    );
}

/**
 * Convierte `span` en el valor responsive de gridColumn para sx de MUI.
 * span=6            -> "span 6" en todos los breakpoints
 * {xs:12, md:6}     -> { xs: "span 12", md: "span 6" }
 */
function normalizeSpan(span) {
    if (typeof span === "number") {
        return `span ${span}`;
    }
    return Object.fromEntries(
        Object.entries(span).map(([bp, cols]) => [bp, `span ${cols}`])
    );
}

export default Panel;
